/**
 * Central Registry for SAFECARE Simulator Scenarios
 * Consolidates L1 (Fiscal), L2 (Events), and L3 (Structural) definitions.
 */

import { defaultSafecareConfig, getGreatStagnationConfig } from './policyConfig.js';

// Re-export specific configs that are used as baselines
export { defaultSafecareConfig };

/**
 * Registry of all available scenarios
 * 
 * Structure:
 * {
 *   id: string,
 *   label: string,
 *   icon: string,
 *   description: string,
 *   details: string, // Markdown-supported tooltip content
 *   group: 'fiscal' | 'social' | 'risk' | 'extreme',
 *   
 *   // Level 1: Policy Configuration Overrides
 *   configOverrides: Partial<SafecareConfig>,
 *   
 *   // Level 2: Event Overrides
 *   eventOverrides: Record<string, { enabled: boolean, [key: string]: any }>,
 *   
 *   // Level 3: Structural/Compounding Logic Overrides
 *   // This is a FUNCTION that modifies the compounding logic context
 *   structuralOverrides: (baseContext) => modifiedContext
 * }
 */
export const SCENARIO_REGISTRY = [
    // ========================================================================
    // 0. BASELINE
    // ========================================================================
    {
        id: 'default',
        label: 'Statutory Default',
        icon: '📜',
        description: 'As written in the SAFECARE Act.',
        details: "**The Baseline:** Assumes full implementation of the law as written, with standard CBO-aligned economic assumptions. \n\n*   **Tax Compliance:** 88% (Standard)\n*   **Admin Savings:** 2% Target reached in 10 years\n*   **Drug Pricing:** IRP + DVB effective",
        group: 'baseline',
        configOverrides: {}, // No overrides, uses default
        eventOverrides: {},
        structuralOverrides: null // No structural changes
    },

    // ========================================================================
    // GROUP 1: FISCAL & STRUCTURAL
    // ========================================================================
    {
        id: 'deficit_hawk',
        label: 'Deficit Hawk',
        icon: '🦅',
        description: 'Higher taxes & strict efficiency to ensure solvency.',
        details: "**Fiscal Discipline:** Prioritizes solvency above all else. \n\n*   **Employer Tax:** 10.2% (vs 8%)\n*   **Individual Tax:** 4.2% (vs 3.5%)\n*   **Provider Pay:** 95% of Medicare\n*   **Loophole Closing:** Aggressive (15% evasion assumed resolved)",
        group: 'fiscal',
        configOverrides: {
            employerRate: 0.102,
            baseIndividualRate: 0.042,
            generalFundPolicy: 'none',
            taxEvasionRate: 0.15,
            providerPaymentFloor: 0.95
        },
        eventOverrides: {}
    },
    {
        id: 'cost_cutter',
        label: 'Bare-Bones Cost Cutter',
        icon: '✂️',
        description: 'Massive spending cuts to verify reserves.',
        details: "**Austerity Mode:** Slashes spending to build massive reserves. \n\n*   Drug innovation reduced by 40%\n*   Provider pay floor: 90%\n*   **Admin Efficiency:** Forced to 8% (Optimistic)\n*   **Stabilizers:** Allowed to cut benefits if needed",
        group: 'fiscal',
        configOverrides: {
            adminEfficiencyFloor: 0.08,
            drugPricingMode: 'bill',
            internationalPricePctOfUS: 0.35,
            domesticValuePricePctOfUS: 0.55,
            enforceIRP: 1,
            enforceDrugCeilings: 1,
            drugInnovationReduction: 0.40,
            providerPaymentFloor: 0.90,
            annualCopaymentCap: 1500,
            allowAutoIncrease: true,
            allowAutoDecrease: true
        }
    },
    {
        id: 'gentle_transition',
        label: 'Gentle Transition',
        icon: 'shaking_hands', // standard emoji text
        description: 'Soft landing for industry.',
        details: "**Industry Friendly:** Slows the transition to protect providers. \n\n*   **Provider Pay:** 115% of Medicare\n*   **Severance:** 9 months (vs 6)\n*   **Legal Delays:** None assumed (Cooperative)",
        group: 'fiscal',
        configOverrides: {
            providerPaymentFloor: 1.15,
            generalFundPolicy: 'phase-down',
            legalChallengeDelay: 0,
            severanceMonths: 9
        }
    },

    // ========================================================================
    // GROUP 2: SOCIAL & EQUITY
    // ========================================================================
    {
        id: 'equity_max',
        label: 'Equity-Max',
        icon: '⚖️',
        description: 'Maximum redistribution and coverage.',
        details: "**Progressive Dream:** maximizing coverage and redistribution. \n\n*   **Capital Gains:** 100% included in tax base\n*   **Wealth Tax:** Aggressive surtaxes (3-8%)\n*   **Copay Cap:** $250/yr (vs $750)\n*   **Retirement:** $50k exemption",
        group: 'social',
        configOverrides: {
            capitalCoverage: 1.0,
            baseIndividualRate: 0.045,
            surtaxRates: [0.03, 0.05, 0.08],
            annualCopaymentCap: 250,
            retirementExemptionLimit: 50000
        }
    },
    {
        id: 'education_heavy',
        label: 'Education-Heavy',
        icon: '🎓',
        description: 'Invests heavily in future workforce.',
        details: "**Workforce Investment:** Shifts spending to education and training. \n\n*   **Tuition Cap:** $8k/yr\n*   **Debt Redemption:** $20B/yr\n*   **Retraining:** High funding level",
        group: 'social',
        configOverrides: {
            tuitionCapAnnual: 8000,
            tuitionSupportCoverage: 1.0,
            retrainingFundingLevel: 'high',
            debtRedemptionBudget: 20000000000
        }
    },
    {
        id: 'rural_lifeline',
        label: 'Rural Lifeline',
        icon: '🚜',
        description: 'Massive support for rural health.',
        details: "**Rural Protection:** Ensures rural access at all costs. \n\n*   **Rural Uplift:** +50% payment\n*   **Capital Grants:** $200B/yr (Doubled)\n*   **Closures:** Limited to 2% (vs 8% baseline)",
        group: 'social',
        configOverrides: {
            ruralUplift: 1.50,
            capitalGrantsAnnual: 200000000000,
            ruralHospitalClosureRate: 0.02
        }
    },

    // ========================================================================
    // GROUP 3: REALISM & RISKS
    // ========================================================================
    {
        id: 'state_resistance',
        label: 'State Resistance',
        icon: '🚧',
        description: 'Political obstruction delays implementation.',
        details: "**Gridlock:** States refuse to cooperate. \n\n*   **Cooperation:** 60%\n*   **Legal Delay:** 2 Years\n*   **Admin Efficiency:** Stuck at 7.5%",
        group: 'risk',
        configOverrides: {
            stateCooperation: 0.60,
            adminEfficiencyFloor: 0.075,
            legalChallengeDelay: 2
        }
    },
    {
        id: 'innovation_sensitive',
        label: 'Innovation-Sensitive',
        icon: '🧪',
        description: 'Protects pharma profits to ensure R&D.',
        details: "**Pharma Protection:** Weaker price controls to safeguard R&D. \n\n*   **IRP Enforcement:** 50% effective\n*   **Price Anchors:** 90% of US (vs 40%)\n*   **Innovation Hit:** Only 10% reduction",
        group: 'risk',
        configOverrides: {
            enforceIRP: 0.5,
            startYear: 2027, // Can push back?
            drugInnovationReduction: 0.10,
            smallInnovatorBudget: 2000000000,
            drugPricingMode: 'bill',
            internationalPricePctOfUS: 0.90,
            domesticValuePricePctOfUS: 0.90,
            allowAutoIncrease: true, // Statues hold
            allowAutoDecrease: true
        }
    },
    {
        id: 'high_capital_carve_out',
        label: 'Capital Carve-Out',
        icon: '💼',
        description: 'Protects investment income.',
        details: "**Investor Shield:** Limits exposure of capital gains. \n\n*   **Capital Coverage:** 40% (vs 100%)\n*   **Pass-Through:** 80% coverage",
        group: 'risk',
        configOverrides: {
            capitalCoverage: 0.40,
            passThroughCoverage: 0.80
        }
    },

    // ========================================================================
    // GROUP 4: EXTREME & NEW SCENARIOS
    // ========================================================================
    {
        id: 'full_obstruction',
        label: 'Full Obstruction',
        icon: '🛑',
        description: 'Maximum failure conditions.',
        details: "**Doomsday Scenario:** Everything goes wrong. \n\n*   **Legal Delay:** 4 Years\n*   **State Coop:** 20%\n*   **Stabilizers:** Blocked\n*   **Drug Pricing:** Machinary fails",
        group: 'extreme',
        configOverrides: {
            stateCooperation: 0.20,
            taxEvasionRate: 0.25,
            providerPaymentFloor: 1.30,
            legalChallengeDelay: 4,
            enforceTrustFundRules: 0,
            allowAutoIncrease: false,
            allowAutoDecrease: false,
            drugPricingMode: 'dvb_only',
            enforceIRP: 0,
            enforceDrugCeilings: 0.3,
            internationalPricePctOfUS: 0.60,
            domesticValuePricePctOfUS: 0.85,
            enforceAdminSimplification: 0
        }
    },
    {
        id: 'stagnation',
        label: 'The Great Stagnation',
        icon: '📉',
        description: 'Structural economic failure (L3 Overlay).',
        details: "**Structural Decay:** A deep L3 override scenario. \n\n*   **Wage Growth:** Workers reject net-pay arguments\n*   **Admin Efficiency:** Stuck at 12%\n*   **Global Event:** Economic stagnation trigger",
        group: 'extreme',
        configOverrides: getGreatStagnationConfig(), // Uses the helper from policyConfig
        eventOverrides: {
            global_stagnation: { enabled: true, triggerYear: 2027, severity: 1.0 }
        },
        structuralOverrides: (baseContext) => {
            // This mirrors applyStagnationOverrides but in a functional way
            // We return a "patch" or instruction to the compounding engine
            return {
                netVsGrossPayEffect: { enabled: false },
                administrativeComplexityCascadeEffect: {
                    simplifiedSystem: { targetAdminPercent: 0.12 }
                }
            };
        }
    },
    {
        id: 'utopia',
        label: 'Utopia',
        icon: '🌈',
        description: 'Perfect execution of all parameters.',
        details: "**Best Case:** Everything works perfectly. \n\n*   **Tax Evasion:** 5%\n*   **Admin Efficiency:** 2%\n*   **Utilization Surge:** Low (10%)",
        group: 'extreme',
        configOverrides: {
            taxEvasionRate: 0.05,
            adminEfficiencyFloor: 0.02,
            stateCooperation: 1.0,
            providerParticipationRate: 1.0,
            utilizationSurge: 0.10,
            providerQualityDecline: 0.0,
            allowAutoIncrease: true,
            allowAutoDecrease: true,
            drugPricingMode: 'bill',
            internationalPricePctOfUS: 0.35,
            domesticValuePricePctOfUS: 0.55,
            enforceIRP: 1,
            enforceDrugCeilings: 1,
            enforceAdminSimplification: 1
        }
    },

    // ========================================================================
    // NEW SCENARIOS
    // ========================================================================
    {
        id: 'pandemic_shock',
        label: 'Pandemic Shock',
        icon: '🦠',
        description: 'High utilization surge, revenue drop.',
        details: "**Public Health Crisis:** Modeled on COVID-like disruption. \n\n*   **Utilization:** +30% Surge\n*   **New Revenue:** -5% (Recession)\n*   **Rural Closures:** Accelerate to 15%",
        group: 'risk',
        configOverrides: {
            utilizationSurge: 0.30,
            taxEvasionRate: 0.18, // Compliance drops
            ruralHospitalClosureRate: 0.15,
            providerPaymentFloor: 1.10 // Stabilization payments
        },
        eventOverrides: {
            pandemic_event: { enabled: true, triggerYear: 2029, severity: 0.8 }
        }
    },
    {
        id: 'ai_revolution',
        label: 'AI Revolution',
        icon: '🤖',
        description: 'Max efficiency, drug discovery boost.',
        details: "**Tech Singularity:** AI solves admin and biology. \n\n*   **Admin Floor:** 1% (Hyper-efficient)\n*   **Drug Innovation:** +20% (vs reduction)\n*   **Fraud:** 100% detection",
        group: 'social', // Or a new 'tech' group? Sticking to existing for now or risk
        configOverrides: {
            adminEfficiencyFloor: 0.01,
            drugInnovationReduction: -0.20, // Negative reduction = Increase!
            aiFraudIntensity: 1.5, // Super-human detection
            productivityIncrease: 0.60 // 60% more patients/doc
        }
    }
];

export function getScenarioById(id) {
    return SCENARIO_REGISTRY.find(s => s.id === id);
}

export function getScenariosByGroup(group) {
    return SCENARIO_REGISTRY.filter(s => s.group === group);
}
